import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import Header from "@/components/landing/Header";

const PAIRS = ["BTC/USDT", "ETH/USDT", "SOL/USDT"];

export default function FuturesTrade() {
  const { pair: routePair } = useParams();
  const [pair, setPair] = useState(routePair?.replace("-", "/") || "BTC/USDT");
  const [marks, setMarks] = useState({});
  const [positions, setPositions] = useState([]);
  const [side, setSide] = useState("long");
  const [qty, setQty] = useState("0.001");
  const [leverage, setLeverage] = useState(5);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const [m, p] = await Promise.all([
        api.get("/api/futures/mark-prices"),
        api.get("/api/futures/positions"),
      ]);
      const map = {};
      (m.data.mark_prices || []).forEach((row) => {
        map[row.pair] = row.mark_price;
      });
      setMarks(map);
      setPositions(p.data.positions || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const openPosition = async () => {
    setBusy(true);
    setError("");
    try {
      await api.post("/api/futures/positions/open", {
        pair,
        side,
        qty,
        leverage,
        margin_mode: "isolated",
      });
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const closePosition = async (positionId) => {
    setBusy(true);
    try {
      await api.post(`/api/futures/positions/${positionId}/close`);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || "Close failed");
    } finally {
      setBusy(false);
    }
  };

  const mark = marks[pair];

  return (
    <div className="min-h-screen bg-[var(--cm-bg)]">
      <Header />
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-cm-navy">Futures</h1>
            <p className="text-sm text-cm-grey">
              Perpetual contracts — isolated margin. Not the same as spot portfolio margin-health.
            </p>
          </div>
          <Link to="/trade/spot/BTC-USDT" className="text-sm text-cm-blue font-semibold">
            Spot trading →
          </Link>
        </div>

        <div className="bg-white rounded-xl border border-[var(--cm-border)] p-5 space-y-4">
          <div className="flex flex-wrap gap-3">
            <select
              value={pair}
              onChange={(e) => setPair(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm"
            >
              {PAIRS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
            <span className="text-sm text-cm-grey self-center">
              Mark: <b>{mark ? Number(mark).toLocaleString() : "—"}</b>
            </span>
          </div>

          <div className="flex gap-2">
            {["long", "short"].map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSide(s)}
                className={`flex-1 py-2 rounded-lg font-semibold text-sm ${
                  side === s
                    ? s === "long"
                      ? "bg-emerald-600 text-white"
                      : "bg-red-500 text-white"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                {s.toUpperCase()}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs text-cm-grey">
              Size (base)
              <input
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                className="w-full mt-1 border rounded-lg px-3 py-2 text-sm"
              />
            </label>
            <label className="text-xs text-cm-grey">
              Leverage
              <input
                type="number"
                min={1}
                max={20}
                value={leverage}
                onChange={(e) => setLeverage(Number(e.target.value))}
                className="w-full mt-1 border rounded-lg px-3 py-2 text-sm"
              />
            </label>
          </div>

          {error && <p className="text-sm text-red-600">{typeof error === "string" ? error : JSON.stringify(error)}</p>}

          <button
            type="button"
            disabled={busy}
            onClick={openPosition}
            className="w-full py-3 rounded-lg bg-cm-blue text-white font-bold disabled:opacity-50"
          >
            {busy ? "Submitting…" : "Open position"}
          </button>
        </div>

        <div className="bg-white rounded-xl border border-[var(--cm-border)] p-5">
          <h2 className="font-bold text-cm-navy mb-3">Open positions</h2>
          {positions.length === 0 ? (
            <p className="text-sm text-cm-grey">No open futures positions.</p>
          ) : (
            <div className="space-y-3">
              {positions.map((p) => (
                <div
                  key={p.position_id}
                  className="flex flex-wrap justify-between gap-2 border-b border-gray-100 pb-3"
                >
                  <div>
                    <div className="font-semibold">
                      {p.side.toUpperCase()} {p.pair} · {p.leverage}x
                    </div>
                    <div className="text-xs text-cm-grey">
                      Qty {p.qty} @ {p.entry_price} · Mark {p.mark_price}
                    </div>
                    <div className="text-xs">
                      uPnL {p.unrealized_pnl} · Margin ratio {p.margin_ratio_pct?.toFixed(1)}%
                    </div>
                    <div className="text-xs text-amber-700">Liq ~ {p.liquidation_price}</div>
                  </div>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => closePosition(p.position_id)}
                    className="self-start px-3 py-1.5 text-xs font-bold border rounded-lg"
                  >
                    Close
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
